import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridRowProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  deletePurchaseOrder,
  getAllPurchaseOrders,
} from "./services/purchaseOrder.services";
import {
  clearFilterCustomPO,
  clearPurchaseOrderID,
  setFilterCustomPO,
  setPurchaseOrderID,
} from "./purchaseOrderSlice";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  FINANCIAL_YEAR,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import { clearItemList } from "../Item/itemSlice";
import _debounce from "lodash/debounce";
import { TextBox } from "@progress/kendo-react-inputs";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import { IoPrint } from "react-icons/io5";

const PurchaseOrder: React.FC = () => {
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const handleCreate = () => {
    navigate("/purchaseorder/create");
  };

  return (
    <>
      <DeletePurchaseOrderDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Purchase Order List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <PurchaseOrderGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const PurchaseOrderGridComponent: React.FC = () => {
  const isEditBtnRef = useRef<any>(false);
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const [expandedRow, setExpandedRow] = React.useState(null);
  const PurchaseOrderList = useAppSelector(
    (state) => state.purchaseOrder.PurchaseOrderList
  );
  const loading = useAppSelector((state) => state.purchaseOrder.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomPO: any = useAppSelector(
    (state) => state.purchaseOrder.FilterCustomPO
  );
  useEffect(() => {
    return () => {
      dispatch(clearItemList());
      dispatch(setCurrentPage(0));
      if (!isEditBtnRef.current) {
        dispatch(clearFilterCustomPO());
      }
    };
  }, []);

  useEffect(() => {
    const payload = {
      ...FilterCustomPO,
      financial_account_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllPurchaseOrders(payload));
  }, [currentPage, pageLimit]);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (purchase_order_guid: number) => {
      navigate("/purchaseorder/edit", {
        state: { purchase_order_guid: purchase_order_guid },
      });
    };
    const handleOpenDeleteDialog = (purchase_order_guid: number) => {
      dispatch(openDialog("deletePurchaseOrder"));
      dispatch(setPurchaseOrderID(purchase_order_guid));
    };

    const handlePurchaseOrderPrint = (purchase_order_guid: string) => {
      window.open(`/purchaseorderprint/${purchase_order_guid}`);
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            ref={isEditBtnRef}
            onClick={() => {
              isEditBtnRef.current = true;
              handleEdit(props.dataItem?.purchase_order_guid);
            }}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem?.purchase_order_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Print"
            onClick={() =>
              handlePurchaseOrderPrint(props.dataItem?.purchase_order_guid)
            }
          >
            <IoPrint
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const rowRender = (
    trElement: React.ReactElement<HTMLTableRowElement>,
    props: GridRowProps
  ) => {
    const isdelivered = props.dataItem.isdelivered;
    const deliver = { backgroundColor: "#ffdada" };
    const notDelivered = { backgroundColor: "transparent" };
    const trProps: any = { style: isdelivered ? notDelivered : deliver };
    let childrenArray: React.ReactNode[] = [];

    if (trElement.props.children) {
      if (trElement.props.children instanceof HTMLCollection) {
        childrenArray = Array.from(trElement.props.children).map(
          (child: any) => {
            return React.isValidElement(child) ? child : <>{child}</>;
          }
        );
      } else {
        childrenArray = [trElement.props.children];
      }
    }

    return React.cloneElement(trElement, { ...trProps }, childrenArray);
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header`}
      // className={`table-header ${props.index === 8 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );
  const truncateRemarks = (remarks: string) => {
    if (!remarks) return "";
    const maxLength = 100;
    if (remarks.length <= maxLength) return remarks;
    return remarks.substring(0, maxLength) + "...";
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        rowRender={rowRender}
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={filter ? filterBy(PurchaseOrderList, filter) : PurchaseOrderList}
        onFilterChange={handleFilterChange}
        total={
          PurchaseOrderList[0]?.total_item_count || PurchaseOrderList.length
        }
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="po_no"
          title="PO No"
          width={120}
          filterable={true}
          cell={(props: any) => (
            <td>
              <div
                className={isEditAccess ? "hoverableName" : "normalName"}
                onClick={() =>
                  isEditAccess &&
                  navigate("/purchaseorder/edit", {
                    state: {
                      purchase_order_guid: props.dataItem?.purchase_order_guid,
                    },
                  })
                }
              >
                {props.dataItem?.po_no}
              </div>
            </td>
          )}
        />
        <Column field="po_date" title="PO Date" width={150} />
        <Column
          width={200}
          field="account_name"
          title="Vendor"
          filterable
          filterCell={CommonFilterCell}
        />
        <Column
          field="gst_no"
          title="GST No"
          filterCell={CommonFilterCell}
          width={200}
        />

        {/* <Column
          field="quotation_no"
          title="Quotation No"
          cell={(props: any) => (
            <td className="text-end">{props.dataItem?.quotation_no}</td>
          )}
        /> */}
        <Column
          width={150}
          field="taxable_amount"
          title="Total Amount"
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem.taxable_amount
                ? formatIndianNumber(props.dataItem.taxable_amount)
                : null}
            </td>
          )}
        />
        <Column
          width={150}
          field="gst"
          title="GST Amount"
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem.gst
                ? formatIndianNumber(props.dataItem.gst)
                : null}
            </td>
          )}
        />
        <Column
          width={150}
          field="ground_total"
          title="Grand Amount"
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem.ground_total
                ? formatIndianNumber(props.dataItem.ground_total)
                : null}
            </td>
          )}
        />
        <Column
          field="remarks"
          title="Remarks"
          width={200}
          cell={(props: any) => (
            <td>
              <div
                className="remarks-content"
                dangerouslySetInnerHTML={{
                  __html:
                    expandedRow === props.dataItem?.id
                      ? props.dataItem?.remarks
                      : truncateRemarks(props.dataItem?.remarks),
                }}
              />
              {props.dataItem?.remarks.length > 100 && (
                <span
                  className="readmore-btn"
                  onClick={() =>
                    setExpandedRow(
                      expandedRow === props.dataItem?.id
                        ? null
                        : props.dataItem?.id
                    )
                  }
                >
                  {expandedRow === props.dataItem?.id
                    ? "Read Less"
                    : "Read More"}
                </span>
              )}
            </td>
          )}
        />
        {(isEditAccess || isDeleteAccess || isPrintAccess) && (
          <Column
            field="id"
            title="Actions"
            cell={MyEditCommandCell}
            width={"140px"}
            // locked={true}
            filterable={false}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeletePurchaseOrderDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const PurchaseOrderID = useAppSelector(
    (state) => state.purchaseOrder.PurchaseOrderID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomPO: any = useAppSelector(
    (state) => state.purchaseOrder.FilterCustomPO
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deletePurchaseOrder(ID));
      dispatch(setCurrentPage(0));
      const payload = {
        ...FilterCustomPO,
        financial_account_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      if (response.payload.Data.Status === 200) {
        dispatch(getAllPurchaseOrders(payload));
        dispatch(closeDialog());
        dispatch(clearPurchaseOrderID());
      } else {
        dispatch(getAllPurchaseOrders(payload));
        dispatch(closeDialog());
        dispatch(clearPurchaseOrderID());
      }
    }
  };

  return (
    <>
      {dialogName === "deletePurchaseOrder" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Purchase Order"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Purchase Order?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(PurchaseOrderID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomPO: any = useAppSelector(
    (state) => state.purchaseOrder.FilterCustomPO
  );
  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        financial_account_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: 0 * +pageLimit,
      };

      dispatch(getAllPurchaseOrders(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = { ...FilterCustomPO, [inputField]: inputValue };
      dispatch(setFilterCustomPO(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterCustomPO, [props.field]: "" };
    dispatch(setFilterCustomPO(updatedFilters));

    const payload = {
      ...updatedFilters,
      financial_account_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllPurchaseOrders(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterCustomPO[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterCustomPO[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

export default PurchaseOrder;
